<template>
    <Card pageTitle="New Price Adjustment">
        <AllowPermission :permission="PERMISSION_CODES.CMS_CREATE_ADJUSTMENT">
            <BackNavigateBtn title="Back to Price Adjustments" to="/inventory-adjustments/price" />

            <div class="create-adjustment-container">
                <!-- Enhanced Product Search Section -->
                <div class="product-search-section">
                    <div class="section-header">
                        <h4><i class="fas fa-search mr-2"></i>Add Products to Adjust</h4>
                        <p class="section-subtitle">Search and select products to adjust their prices</p>
                    </div>
                    <PriceAdjustmentProductAdder :addedProducts="addedProducts" @add="handleAdd" />
                </div>

                <!-- Enhanced Products Table -->
                <div class="products-table-section">
                    <div class="section-header">
                        <h4><i class="fas fa-list mr-2"></i>Products for Price Adjustment</h4>
                        <p class="section-subtitle">Review and adjust prices for selected products</p>
                    </div>
                    
                    <div class="products-table-container">
                        <div class="table-responsive">
                            <table class="custom-products-table">
                                <thead>
                                    <tr>
                                        <th class="action-col">
                                            <i class="fas fa-times"></i>
                                        </th>
                                        <th class="product-info-col">
                                            <i class="fas fa-box mr-1"></i>Product Details
                                        </th>
                                        <th class="warehouse-col">
                                            <i class="fas fa-warehouse mr-1"></i>Warehouse
                                        </th>
                                        <th class="price-adjustment-col">
                                            <i class="fas fa-dollar-sign mr-1"></i>Price Adjustment
                                        </th>
                                        <th class="reason-notes-col">
                                            <i class="fas fa-comment mr-1"></i>Reason & Notes
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="product of addedProducts" :key="product.uniqueId" class="product-row">
                                        <td class="action-cell">
                                            <button @click="removeProduct(product)" class="remove-btn">
                                                <i class="fas fa-times" />
                                            </button>
                                        </td>
                                        <td class="product-info-cell">
                                            <div class="product-details">
                                                <div class="product-name">{{ product.item_name }}</div>
                                                <div class="product-meta">
                                                    <span class="sku-badge">{{ product.item_code }}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td class="warehouse-cell">
                                            <div class="warehouse-badge">{{ product.warehouse_name }}</div>
                                        </td>
                                        <td class="price-adjustment-cell">
                                            <div class="price-adjustment-container">
                                                <div class="current-price-row">
                                                    <span class="price-label">Current:</span>
                                                    <span class="price-value current">
                                                        ${{ formatCurrency(product.current_price) }}
                                                    </span>
                                                </div>
                                                <div class="new-price-row">
                                                    <span class="price-label">New:</span>
                                                    <CustomInput 
                                                        type="number" 
                                                        step="0.01" 
                                                        min="0" 
                                                        small 
                                                        v-model="product.new_price" 
                                                        @input="updatePriceChange(product)"
                                                        :class="getPriceChangeClass(product.current_price, product.new_price)"
                                                        placeholder="0.00"
                                                        class="compact-input"
                                                    />
                                                </div>
                                                <div class="change-row">
                                                    <div class="change-container" :class="getPriceChangeValueClass(product.current_price, product.new_price)">
                                                        <i :class="getPriceChangeIcon(product.current_price, product.new_price)" class="mr-1"></i>
                                                        {{ calculatePriceChangePercentage(product.current_price, product.new_price) }}%
                                                        ({{ getPriceChangeSymbol(product.current_price, product.new_price) }}${{ formatCurrency(Math.abs(Number(product.new_price) - Number(product.current_price))) }})
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td class="reason-notes-cell">
                                            <div class="reason-notes-container">
                                                <div class="reason-row">
                                                    <select 
                                                        v-model="product.reason_code_id" 
                                                        class="enhanced-select compact-select"
                                                    >
                                                        <option disabled value="">Select reason</option>
                                                        <optgroup v-for="category in categories" :key="category.name" :label="category.label">
                                                            <option 
                                                                v-for="reason in reasonsByCategory[category.name]" 
                                                                :key="reason.id" 
                                                                :value="reason.id"
                                                                :title="reason.description"
                                                            >
                                                                {{ reason.name }}
                                                            </option>
                                                        </optgroup>
                                                    </select>
                                                    <div v-if="getReasonDescription(product.reason_code_id)" class="reason-tooltip">
                                                        <i class="fas fa-info-circle"></i>
                                                        <div class="tooltip-text">
                                                            {{ getReasonDescription(product.reason_code_id) }}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="notes-row">
                                                    <CustomInput 
                                                        small 
                                                        v-model="product.notes" 
                                                        placeholder="Notes..."
                                                        class="compact-input"
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        
                        <div v-if="!addedProducts.length" class="empty-products-state">
                            <div class="empty-icon">
                                <i class="fas fa-dollar-sign"></i>
                            </div>
                            <h5>No Products Added Yet</h5>
                            <p>Use the search above to find and add products for price adjustment</p>
                        </div>
                    </div>
                </div>

                <!-- Enhanced Summary Section -->
                <div v-if="addedProducts.length" class="adjustment-summary-section">
                    <div class="section-header">
                        <h4><i class="fas fa-chart-line mr-2"></i>Adjustment Summary</h4>
                        <p class="section-subtitle">Overview of price changes and financial impact</p>
                    </div>
                    
                    <div class="summary-cards">
                        <div class="summary-card">
                            <div class="summary-icon">
                                <i class="fas fa-box"></i>
                            </div>
                            <div class="summary-content">
                                <div class="summary-label">Total Items</div>
                                <div class="summary-value">{{ addedProducts.length }}</div>
                            </div>
                        </div>
                        
                        <div class="summary-card increase">
                            <div class="summary-icon">
                                <i class="fas fa-arrow-up"></i>
                            </div>
                            <div class="summary-content">
                                <div class="summary-label">Price Increase</div>
                                <div class="summary-value">+${{ formatCurrency(calculateTotalIncrease()) }}</div>
                            </div>
                        </div>
                        
                        <div class="summary-card decrease">
                            <div class="summary-icon">
                                <i class="fas fa-arrow-down"></i>
                            </div>
                            <div class="summary-content">
                                <div class="summary-label">Price Decrease</div>
                                <div class="summary-value">-${{ formatCurrency(calculateTotalDecrease()) }}</div>
                            </div>
                        </div>
                        
                        <div class="summary-card net" :class="getNetChangeClass()">
                            <div class="summary-icon">
                                <i class="fas fa-balance-scale"></i>
                            </div>
                            <div class="summary-content">
                                <div class="summary-label">Net Change</div>
                                <div class="summary-value">
                                    {{ calculateNetChange() >= 0 ? '+' : '' }}${{ formatCurrency(calculateNetChange()) }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Enhanced Action Buttons -->
                <div class="action-buttons-section">
                    <div class="buttons-container">
                        <router-link to="/inventory-adjustments/price">
                            <Button class="cancel-btn">
                                <i class="fas fa-times mr-2"></i>Cancel
                            </Button>
                        </router-link>
                        <Button 
                            @click="showConfirmation" 
                            :disabled="!addedProducts.length || savingPriceAdjustment"
                            class="save-btn"
                            primary
                        >
                            <i class="fas fa-save mr-2"></i>
                            {{ !savingPriceAdjustment ? "Review & Save" : "Saving..." }}
                        </Button>
                    </div>
                </div>
            </div>
                
                <!-- Confirmation Modal -->
                <CustomModal :show="showConfirmationModal" title="Confirm Price Adjustment" @close="showConfirmationModal = false" class="high-positioned-modal">
                    <div class="uniform-confirmation-content">
                        <!-- Summary Header -->
                        <div class="confirmation-summary-header">
                            <div class="summary-stats">
                                <div class="stat-item">
                                    <span class="stat-label">Total Items</span>
                                    <span class="stat-value">{{ addedProducts.length }}</span>
                                </div>
                                <div class="stat-item">
                                    <span class="stat-label">Price Increase</span>
                                    <span class="stat-value positive">+${{ formatCurrency(calculateTotalIncrease()) }}</span>
                                </div>
                                <div class="stat-item">
                                    <span class="stat-label">Price Decrease</span>
                                    <span class="stat-value negative">-${{ formatCurrency(calculateTotalDecrease()) }}</span>
                                </div>
                                <div class="stat-item">
                                    <span class="stat-label">Net Change</span>
                                    <span class="stat-value" :class="calculateNetChange() >= 0 ? 'positive' : 'negative'">
                                        {{ calculateNetChange() >= 0 ? '+' : '' }}${{ formatCurrency(Math.abs(calculateNetChange())) }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Product Cards -->
                        <div class="confirmation-products">
                            <div v-for="product in addedProducts" :key="product.uniqueId" class="product-confirmation-card">
                                <div class="product-card-header">
                                    <div class="product-title">
                                        <h5>{{ product.item_name }}</h5>
                                        <span class="product-sku">{{ product.item_code }}</span>
                                    </div>
                                    <div class="price-change-badge" :class="getPriceChangeBadgeClass(product.current_price, product.new_price)">
                                        <i :class="getPriceChangeIcon(product.current_price, product.new_price)"></i>
                                        {{ calculatePriceChangePercentage(product.current_price, product.new_price) }}%
                                    </div>
                                </div>
                                
                                <div class="product-card-body">
                                    <div class="detail-grid">
                                        <div class="detail-item">
                                            <span class="detail-label">Warehouse</span>
                                            <span class="detail-value">{{ product.warehouse_name }}</span>
                                        </div>
                                        <div class="detail-item">
                                            <span class="detail-label">Current Price</span>
                                            <span class="detail-value">${{ (Number(product.current_price) || 0).toFixed(2) }}</span>
                                        </div>
                                        <div class="detail-item">
                                            <span class="detail-label">New Price</span>
                                            <span class="detail-value">${{ (Number(product.new_price) || 0).toFixed(2) }}</span>
                                        </div>
                                        <div class="detail-item">
                                            <span class="detail-label">Change Amount</span>
                                            <span class="detail-value" :class="getPriceChangeValueClass(product.current_price, product.new_price)">
                                                {{ getPriceChangeSymbol(product.current_price, product.new_price) }}${{ (Math.abs(Number(product.new_price) - Number(product.current_price)) || 0).toFixed(2) }}
                                            </span>
                                        </div>
                                        <div class="detail-item" v-if="product.reason_code_id">
                                            <span class="detail-label">Reason</span>
                                            <span class="detail-value">{{ getReasonName(product.reason_code_id) }}</span>
                                        </div>
                                        <div class="detail-item full-width" v-if="product.notes">
                                            <span class="detail-label">Notes</span>
                                            <span class="detail-value">{{ product.notes }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div class="uniform-confirmation-actions">
                            <Button @click="showConfirmationModal = false" outline>
                                <i class="fas fa-times mr-2"></i>Cancel
                            </Button>
                            <Button @click="handleSave" :disabled="savingPriceAdjustment" primary>
                                <i class="fas fa-check mr-2"></i>
                                {{ !savingPriceAdjustment ? "Confirm Adjustment" : "Saving..." }}
                            </Button>
                        </div>
                    </div>
                </CustomModal>
        </AllowPermission>
    </Card>
</template>

<script>
import { mapState } from "vuex"
import CustomInput from "../../../components/crud/custom-input.vue"
import SelectDropdown from "../../../components/crud/SelectDropdown.vue"
import CustomTable from "../../../components/layout/CustomTable.vue"
import PriceAdjustmentProductAdder from "../../../components/product/PriceAdjustmentProductAdder.vue"
import Button from "../../../components/UI/Button.vue"
import Card from "../../../components/UI/Card.vue"
import PriceFetchApi from "../../../utils/fetch/price-fetch-api"
import { PERMISSION_CODES } from "../../../store/permission"
import AllowPermission from "../../../components/layout/AllowPermission.vue"
import { errMsg } from "../../../utils/common"
import BackNavigateBtn from "../../../components/UI/BackNavigateBtn.vue"
import CustomModal from "../../../components/layout/CustomModal.vue"

export default {
    data() {
        return {
            addedProducts: [],
            priceReasons: [],
            savingPriceAdjustment: false,
            showConfirmationModal: false,
            PERMISSION_CODES,
            categories: [
                { name: 'market', label: 'Market Factors' },
                { name: 'inventory', label: 'Inventory Management' },
                { name: 'promotion', label: 'Promotional Strategy' },
                { name: 'compliance', label: 'Regulatory Compliance' },
                { name: 'quality', label: 'Product Quality' }
            ]
        }
    },
    components: {
        PriceAdjustmentProductAdder,
        CustomInput,
        Card,
        Button,
        SelectDropdown,
        CustomTable,
        AllowPermission,
        BackNavigateBtn,
        CustomModal,
    },
    async mounted() {
        await this.fetchPriceAdjustmentReasons()
    },
    computed: {
        ...mapState("misc", ["warehouses"]),
        // No need for warehouseOptions as warehouse selection is handled in the adder component
        reasonsByCategory() {
            const result = {};
            this.categories.forEach(category => {
                result[category.name] = this.priceReasons.filter(reason => 
                    reason.category === category.name
                );
            });
            return result;
        },
        selectedReason() {
            return this.priceReasons.find(reason => reason.id === this.selectedReasonId);
        }
    },
    methods: {
        formatCurrency(value) {
            return '$' + Number(value || 0).toFixed(2);
        },
        calculatePriceChangePercentage(oldPrice, newPrice) {
            if (!oldPrice) return 0;
            const change = ((newPrice - oldPrice) / oldPrice) * 100;
            return change.toFixed(2);
        },
        calculateTotalIncrease() {
            return this.addedProducts
                .filter(p => Number(p.new_price) > Number(p.current_price))
                .reduce((sum, p) => sum + (Number(p.new_price) - Number(p.current_price)) * (Number(p.qty) || 1), 0);
        },
        calculateTotalDecrease() {
            return this.addedProducts
                .filter(p => Number(p.new_price) < Number(p.current_price))
                .reduce((sum, p) => sum + (Number(p.current_price) - Number(p.new_price)) * (Number(p.qty) || 1), 0);
        },
        calculateNetChange() {
            return this.calculateTotalIncrease() - this.calculateTotalDecrease();
        },
        updatePriceChange(product) {
            // This function updates any derived values when the price changes
            // Currently just a placeholder in case we need to add additional logic
        },
        // Warehouse selection is now handled in the adder component
        getReasonDescription(reasonId) {
            if (!reasonId) return '';
            const reason = this.priceReasons.find(r => r.id === reasonId);
            return reason ? reason.description : '';
        },
        async fetchPriceAdjustmentReasons() {
            try {
                const data = await PriceFetchApi.getPriceAdjustmentReasons();
                this.priceReasons = data.result || [];
                
                // If no specific price adjustment reasons are found, fall back to general reason codes
                if (this.priceReasons.length === 0) {
                    const res = await http.get("/reason-codes");
                    // Map general reason codes to the same format
                    this.priceReasons = res.data.result.map((item) => ({
                        id: item.id,
                        name: item.name,
                        category: 'market', // Default category
                        description: ''
                    }));
                }
            } catch (err) {
                console.log("Error fetching price adjustment reasons", err);
                this.$toast.error("Failed to fetch price adjustment reasons");
                
                // As a fallback, try to fetch general reason codes
                try {
                    const res = await http.get("/reason-codes");
                    this.priceReasons = res.data.result.map((item) => ({
                        id: item.id,
                        name: item.name,
                        category: 'market', // Default category
                        description: ''
                    }));
                } catch (fallbackErr) {
                    console.log("Error fetching fallback reason codes", fallbackErr);
                }
            }
        },
        handleAdd(product) {
            if (product) {
                // The product now comes from PriceAdjustmentProductAdder with warehouse and price already set
                this.addedProducts.push({
                    uniqueId: Date.now(),
                    item_id: product.id,
                    item_name: product.item_name || product.name,
                    item_code: product.item_code,
                    supplier_sku: product.supplier_sku,
                    warehouse_id: product.warehouse_id,
                    warehouse_name: product.warehouse_name,
                    current_price: product.current_price,
                    new_price: product.new_price,
                    reason_code_id: "",
                    notes: "",
                    qty: product.qty || 1 // For calculation purposes
                });
                // Toast is now handled in the adder component
            }
        },
        removeProduct(product) {
            this.addedProducts = this.addedProducts.filter(item => item.uniqueId !== product.uniqueId);
            this.$toast.success("Product removed!");
        },
        getPriceChangeClass(currentPrice, newPrice) {
            if (Number(newPrice) > Number(currentPrice)) return 'price-increase-input'
            if (Number(newPrice) < Number(currentPrice)) return 'price-decrease-input'
            return 'neutral-price-input'
        },
        getPriceChangeValueClass(currentPrice, newPrice) {
            if (Number(newPrice) > Number(currentPrice)) return 'positive'
            if (Number(newPrice) < Number(currentPrice)) return 'negative'
            return 'neutral'
        },
        getPriceChangeIcon(currentPrice, newPrice) {
            if (Number(newPrice) > Number(currentPrice)) return 'fas fa-arrow-up'
            if (Number(newPrice) < Number(currentPrice)) return 'fas fa-arrow-down'
            return 'fas fa-minus'
        },
        getPriceChangeSymbol(currentPrice, newPrice) {
            if (Number(newPrice) > Number(currentPrice)) return '+'
            if (Number(newPrice) < Number(currentPrice)) return '-'
            return ''
        },
        getNetChangeClass() {
            const netChange = this.calculateNetChange()
            if (netChange > 0) return 'positive'
            if (netChange < 0) return 'negative'
            return 'neutral'
        },
        getReasonName(reasonId) {
            const reason = this.priceReasons.find(r => r.id === reasonId)
            return reason ? reason.name : ''
        },
        validateAdjustment() {
            const invalidProducts = this.addedProducts.filter(
                product => !product.warehouse_id || !product.reason_code_id || product.new_price === undefined
            );
            
            if (invalidProducts.length) {
                this.$toast.error("Please complete all required fields for all products!");
                return false;
            }
            
            // Make sure new prices are valid numbers
            const invalidPriceProducts = this.addedProducts.filter(
                product => isNaN(Number(product.new_price)) || Number(product.new_price) < 0
            );
            
            if (invalidPriceProducts.length) {
                this.$toast.error("Please enter valid prices for all products!");
                return false;
            }
            
            return true;
        },
        showConfirmation() {
            if (!this.addedProducts.length) {
                return this.$toast.error("Please add at least one product!");
            }
            
            if (!this.validateAdjustment()) {
                return;
            }
            
            // Check if any prices have actually changed
            const unchangedProducts = this.addedProducts.filter(
                product => Number(product.current_price) === Number(product.new_price)
            );
            
            if (unchangedProducts.length === this.addedProducts.length) {
                return this.$toast.error("Please change at least one price to create an adjustment!");
            }
            
            // Show confirmation modal
            this.showConfirmationModal = true;
        },
        
        getReasonName(reasonId) {
            const reason = this.priceReasons.find(r => r.id === reasonId);
            return reason ? reason.name : '-';
        },
        
        getPriceChangeBadgeClass(currentPrice, newPrice) {
            if (Number(newPrice) > Number(currentPrice)) return 'positive-badge'
            if (Number(newPrice) < Number(currentPrice)) return 'negative-badge'
            return 'neutral-badge'
        },
        async handleSave() {
            if (this.savingPriceAdjustment) return;
            
            // Close the confirmation modal
            this.showConfirmationModal = false;
            
            const postData = this.addedProducts.map(product => ({
                item_id: product.item_id,
                item_name: product.item_name,
                item_code: product.item_code,
                supplier_sku: product.supplier_sku,
                warehouse_id: product.warehouse_id,
                current_price: Number(product.current_price),
                new_price: Number(product.new_price),
                price_reason_id: product.reason_code_id, // Using the specialized price reason ID
                notes: product.notes,
                qty: product.qty || 1
            }));
            
            this.savingPriceAdjustment = true;
            try {
                const data = await PriceFetchApi.createPriceAdjustment(postData);
                this.$toast.success("Price adjustment completed successfully!");
                this.$router.push("/inventory-adjustments/price");
            } catch (err) {
                console.log("Error creating price adjustment", err);
                this.$toast.error(errMsg(err, "Error creating price adjustment!"));
            } finally {
                this.savingPriceAdjustment = false;
            }
        }
    }
}
</script>

<style lang="scss" scoped>
@import "../../../assets/variables.scss";

.create-adjustment-container {
    max-width: 1400px;
    margin: 0 auto;
}

.section-header {
    margin-bottom: 20px;
    
    h4 {
        color: $primaryColor;
        font-weight: 600;
        margin-bottom: 4px;
        display: flex;
        align-items: center;
        
        i {
            color: $secondaryColor;
            font-size: 1.1rem;
        }
    }
    
    .section-subtitle {
        color: rgba($textColor, 0.7);
        font-size: 0.9rem;
        margin: 0;
    }
}

.product-search-section {
    background: $lightCardBg;
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 24px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    border: 1px solid $lightBorderColor;
}

.products-table-section {
    background: $lightCardBg;
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 24px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    border: 1px solid $lightBorderColor;
}

.products-table-container {
    background: $lightCardBg;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid $lightBorderColor;
}

.custom-products-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85rem;
    table-layout: fixed;
    
    thead {
        background: linear-gradient(135deg, $secondaryColor 0%, lighten($secondaryColor, 8%) 100%);
        
        th {
            padding: 14px 10px;
            text-align: left;
            font-weight: 600;
            color: white;
            font-size: 0.8rem;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            border: none;
            
            i {
                opacity: 0.8;
                font-size: 0.75rem;
            }
            
            &.action-col { width: 60px; text-align: center; }
            &.product-info-col { width: 25%; }
            &.warehouse-col { width: 15%; }
            &.price-adjustment-col { width: 30%; }
            &.reason-notes-col { width: 30%; }
        }
    }
    
    tbody {
        tr.product-row {
            border-bottom: 1px solid $lightBorderColor;
            transition: all 0.2s ease;
            
            &:hover {
                background-color: $lightHoverColor;
            }
            
            &:last-child {
                border-bottom: none;
            }
        }
        
        td {
            padding: 12px 10px;
            vertical-align: middle;
            border: none;
        }
    }
}

.action-cell {
    text-align: center;
    
    .remove-btn {
        background: linear-gradient(135deg, $dangerColor 0%, darken($dangerColor, 6%) 100%);
        border: none;
        border-radius: 6px;
        color: white;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.2s ease;
        
        &:hover {
            transform: scale(1.1);
            box-shadow: 0 2px 6px rgba($dangerColor, 0.3);
        }
        
        i {
            font-size: 0.8rem;
        }
    }
}

.product-info-cell {
    .product-details {
        .product-name {
            font-weight: 600;
            color: $primaryColor;
            font-size: 0.9rem;
            line-height: 1.3;
            margin-bottom: 4px;
        }
        
        .product-meta {
            .sku-badge {
                background: linear-gradient(135deg, $lightHoverColor 0%, lighten($lightHoverColor, 4%) 100%);
                padding: 2px 6px;
                border-radius: 3px;
                font-size: 0.75rem;
                font-weight: 500;
                color: $textColor;
                border: 1px solid $lightBorderColor;
            }
        }
    }
}

.warehouse-cell {
    .warehouse-badge {
        background: linear-gradient(135deg, $primaryColor 0%, darken($primaryColor, 8%) 100%);
        color: white;
        padding: 3px 6px;
        border-radius: 3px;
        font-size: 0.75rem;
        font-weight: 500;
        text-align: center;
        display: inline-block;
    }
}

.price-adjustment-cell {
    .price-adjustment-container {
        display: flex;
        flex-direction: column;
        gap: 6px;
        
        .current-price-row,
        .new-price-row {
            display: flex;
            align-items: center;
            gap: 8px;
            
            .price-label {
                font-size: 0.75rem;
                font-weight: 500;
                color: rgba($textColor, 0.7);
                min-width: 55px;
            }
            
            .price-value {
                font-weight: 600;
                font-size: 0.8rem;
                
                &.current {
                    color: rgba($textColor, 0.7);
                    background: $lightHoverColor;
                    padding: 2px 6px;
                    border-radius: 3px;
                    border: 1px solid $lightBorderColor;
                }
            }
            
            .compact-input {
                max-width: 80px !important;
                
                &.price-increase-input {
                    border-color: $successColor;
                    background-color: rgba($successColor, 0.05);
                }
                
                &.price-decrease-input {
                    border-color: $dangerColor;
                    background-color: rgba($dangerColor, 0.05);
                }
                
                &.neutral-price-input {
                    border-color: $lightBorderColor;
                }
            }
        }
        
        .change-row {
            .change-container {
                font-weight: 600;
                font-size: 0.75rem;
                display: flex;
                align-items: center;
                gap: 4px;
                padding: 2px 6px;
                border-radius: 3px;
                
                &.positive {
                    color: $successColor;
                    background: rgba($successColor, 0.1);
                }
                
                &.negative {
                    color: $dangerColor;
                    background: rgba($dangerColor, 0.1);
                }
                
                &.neutral {
                    color: rgba($textColor, 0.7);
                    background: $lightHoverColor;
                }
                
                i {
                    font-size: 0.7rem;
                }
            }
        }
    }
}

.reason-notes-cell {
    .reason-notes-container {
        display: flex;
        flex-direction: column;
        gap: 6px;
        
        .reason-row {
            position: relative;
            display: flex;
            align-items: center;
            
                .compact-select {
                    width: 100%;
                    padding: 4px 8px;
                    border: 1px solid $lightBorderColor;
                    border-radius: 4px;
                    font-size: 0.75rem;
                    background: $lightCardBg;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    
                    &:focus {
                        border-color: $secondaryColor;
                        box-shadow: 0 0 0 0.2rem rgba($secondaryColor, 0.25);
                        outline: none;
                    }
                }
            
            .reason-tooltip {
                margin-left: 6px;
                position: relative;
                
                i {
                    color: $secondaryColor;
                    cursor: help;
                    font-size: 0.8rem;
                }
                
                .tooltip-text {
                    visibility: hidden;
                    opacity: 0;
                    position: absolute;
                    bottom: 100%;
                    left: 50%;
                    transform: translateX(-50%);
                    background: $primaryColor;
                    color: white;
                    padding: 6px;
                    border-radius: 3px;
                    font-size: 0.7rem;
                    white-space: nowrap;
                    z-index: 1000;
                    transition: all 0.2s ease;
                    
                    &::after {
                        content: '';
                        position: absolute;
                        top: 100%;
                        left: 50%;
                        transform: translateX(-50%);
                        border: 3px solid transparent;
                        border-top-color: $primaryColor;
                    }
                }
                
                &:hover .tooltip-text {
                    visibility: visible;
                    opacity: 1;
                }
            }
        }
        
        .notes-row {
            .compact-input {
                width: 100%;
                max-width: none;
            }
        }
    }
}

.empty-products-state {
    text-align: center;
    padding: 60px 20px;
    color: rgba($textColor, 0.7);
    
    .empty-icon {
        margin-bottom: 20px;
        
        i {
            font-size: 3rem;
            color: $lightBorderColor;
        }
    }
    
    h5 {
        color: $primaryColor;
        margin-bottom: 8px;
        font-weight: 600;
    }
    
    p {
        margin: 0;
        font-size: 0.9rem;
    }
}

.adjustment-summary-section {
    background: $lightCardBg;
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 24px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    border: 1px solid $lightBorderColor;
    
    .summary-cards {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
        gap: 16px;
        
        .summary-card {
            background: linear-gradient(135deg, $lightHoverColor 0%, $lightCardBg 100%);
            border: 1px solid $lightBorderColor;
            border-radius: 10px;
            padding: 20px;
            display: flex;
            align-items: center;
            gap: 16px;
            transition: all 0.2s ease;
            
            &:hover {
                transform: translateY(-2px);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            }
            
            .summary-icon {
                width: 48px;
                height: 48px;
                border-radius: 12px;
                display: flex;
                align-items: center;
                justify-content: center;
                background: $lightHoverColor;
                
                i {
                    font-size: 1.2rem;
                    color: white;
                }
            }
            
            .summary-content {
                flex: 1;
                
                .summary-label {
                    color: rgba($textColor, 0.7);
                    font-size: 0.85rem;
                    font-weight: 500;
                    margin-bottom: 4px;
                }
                
                .summary-value {
                    color: $textColor;
                    font-size: 1.4rem;
                    font-weight: 700;
                    line-height: 1;
                }
            }
            
            &.increase {
                .summary-icon {
                    background: linear-gradient(135deg, $successColor 0%, lighten($successColor, 10%) 100%);
                }
                
                .summary-value {
                    color: $successColor;
                }
            }
            
            &.decrease {
                .summary-icon {
                    background: linear-gradient(135deg, $dangerColor 0%, lighten($dangerColor, 10%) 100%);
                }
                
                .summary-value {
                    color: $dangerColor;
                }
            }
            
            &.net {
                .summary-icon {
                    background: linear-gradient(135deg, $secondaryColor 0%, lighten($secondaryColor, 10%) 100%);
                }
                
                &.positive .summary-value {
                    color: $successColor;
                }
                
                &.negative .summary-value {
                    color: $dangerColor;
                }
                
                &.neutral .summary-value {
                    color: rgba($textColor, 0.7);
                }
            }
        }
    }
}

.action-buttons-section {
    background: $lightCardBg;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    border: 1px solid $lightBorderColor;
    
    .buttons-container {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        
        .cancel-btn {
            background: $lightHoverColor;
            border-color: $lightBorderColor;
            color: $textColor;
            
            &:hover {
                background: lighten($lightHoverColor, 3%);
                border-color: lighten($lightBorderColor, 4%);
            }
        }
        
        .save-btn {
            background: linear-gradient(135deg, $secondaryColor 0%, lighten($secondaryColor, 12%) 100%);
            border: none;
            
            &:hover:not(:disabled) {
                background: linear-gradient(135deg, lighten($secondaryColor, 12%) 0%, $secondaryColor 100%);
                transform: translateY(-1px);
                box-shadow: 0 4px 8px rgba(186, 142, 88, 0.2);
            }
            
            &:disabled {
                opacity: 0.6;
                cursor: not-allowed;
            }
        }
    }
}

/* High Positioned Modal Override */
.high-positioned-modal {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    z-index: 1050 !important;
    
    ::v-deep .modal-dialog {
        margin: 2vh auto !important;
        max-width: 800px !important;
        width: 90vw !important;
        max-height: 96vh !important;
        display: flex !important;
        align-items: flex-start !important;
    }
    
    ::v-deep .modal-content {
        max-height: 94vh !important;
        overflow: hidden !important;
        display: flex !important;
        flex-direction: column !important;
        width: 100% !important;
    }
    
    ::v-deep .modal-body {
        flex: 1 !important;
        overflow: hidden !important;
        padding: 16px !important;
    }
    
    ::v-deep .modal-header {
        padding: 16px 20px 12px 20px !important;
        border-bottom: 1px solid $lightBorderColor !important;
    }
}

/* Uniform Confirmation Modal Styles */
.uniform-confirmation-content {
    display: flex;
    flex-direction: column;
    max-height: 82vh;
    overflow-y: auto;
}

.confirmation-summary-header {
    background: linear-gradient(135deg, $lightHoverColor 0%, lighten($lightHoverColor, 4%) 100%);
    border-radius: 8px;
    padding: 12px;
    margin-bottom: 12px;
    border: 1px solid $lightBorderColor;
}

.summary-stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
}

.stat-item {
    text-align: center;
    background: $lightCardBg;
    padding: 16px 12px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border: 1px solid $lightBorderColor;
    
    .stat-label {
        display: block;
        font-size: 0.75rem;
        font-weight: 600;
        color: rgba($textColor, 0.75);
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 8px;
    }
    
    .stat-value {
        display: block;
        font-size: 1.3rem;
        font-weight: 800;
        color: $textColor;
        
        &.positive {
            color: $successColor;
        }
        
        &.negative {
            color: $dangerColor;
        }
    }
}

.confirmation-products {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 12px;
    max-height: 300px;
    overflow-y: auto;
    padding-right: 8px;
}

.product-confirmation-card {
    background: $lightCardBg;
    border: 1px solid $lightBorderColor;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;
    
    &:hover {
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
}

.product-card-header {
    background: linear-gradient(135deg, $secondaryColor 0%, lighten($secondaryColor, 8%) 100%);
    padding: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.product-title {
    h5 {
        color: white;
        margin: 0 0 3px 0;
        font-size: 0.9rem;
        font-weight: 600;
    }
    
    .product-sku {
        color: rgba(255, 255, 255, 0.8);
        font-size: 0.75rem;
        font-weight: 500;
    }
}

.adjustment-badge,
.price-change-badge {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border-radius: 16px;
    font-size: 0.8rem;
    font-weight: 600;
    
    &.positive-badge {
        background: rgba($successColor, 0.2);
        color: darken($successColor, 30%);
        border: 1px solid rgba($successColor, 0.3);
    }
    
    &.negative-badge {
        background: rgba($dangerColor, 0.2);
        color: darken($dangerColor, 25%);
        border: 1px solid rgba($dangerColor, 0.3);
    }
    
    &.neutral-badge {
        background: rgba($textColor, 0.1);
        color: rgba($textColor, 0.7);
        border: 1px solid rgba($textColor, 0.2);
    }
    
    i {
        font-size: 0.7rem;
    }
}

.product-card-body {
    padding: 12px;
}

.detail-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
}

.detail-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px;
    background: rgba($lightHoverColor, 0.6);
    border-radius: 6px;
    border: 1px solid $lightBorderColor;
    
    &.full-width {
        grid-column: 1 / -1;
    }
    
    .detail-label {
        font-size: 0.75rem;
        font-weight: 600;
        color: $textColor;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }
    
    .detail-value {
        font-size: 0.95rem;
        font-weight: 700;
        color: $textColor;
        
        &.positive {
            color: $successColor;
            font-weight: 800;
        }
        
        &.negative {
            color: $dangerColor;
            font-weight: 800;
        }
    }
}

/* Dark theme overrides for the confirmation card */
.dark .product-confirmation-card {
    background: $darkCardBg;
    border-color: $darkBorderColor;
}
.dark .product-card-header {
    background: linear-gradient(135deg, $secondaryColorDark 0%, lighten($secondaryColorDark, 8%) 100%);
}
.dark .detail-item {
    background: lighten($darkCardBg, 3%);
    border-color: $darkBorderColor;
}
.dark .detail-item .detail-label,
.dark .detail-item .detail-value { color: $darkTextColor; }
.dark .detail-item .detail-value.positive { color: $successColorDark; }
.dark .detail-item .detail-value.negative { color: $dangerColorDark; }

/* Dark theme overrides for sections */
.dark .product-search-section,
.dark .products-table-section,
.dark .products-table-container,
.dark .action-buttons-section {
    background: $darkCardBg;
    border-color: $darkBorderColor;
}
.dark .custom-products-table tbody tr:hover {
    background-color: lighten($darkCardBg, 4%);
}
.dark .action-cell .remove-btn {
    background: linear-gradient(135deg, $dangerColorDark 0%, lighten($dangerColorDark, 6%) 100%);
}
.dark .empty-products-state { color: rgba($darkTextColor, 0.75); }
.dark .empty-products-state .empty-icon i { color: $darkBorderColor; }
.dark .empty-products-state h5 { color: $darkTextColor; }

.dark .adjustment-summary-section { background: $darkCardBg; border-color: $darkBorderColor; }
.dark .adjustment-summary-section .summary-card {
    background: linear-gradient(135deg, lighten($darkCardBg, 3%) 0%, lighten($darkCardBg, 6%) 100%);
    border-color: $darkBorderColor;
}
.dark .adjustment-summary-section .summary-card .summary-label { color: rgba($darkTextColor, 0.7); }
.dark .adjustment-summary-section .summary-card .summary-value { color: $darkTextColor; }
.dark .adjustment-summary-section .summary-card.increase .summary-icon {
    background: linear-gradient(135deg, $successColorDark 0%, lighten($successColorDark, 10%) 100%);
}
.dark .adjustment-summary-section .summary-card.decrease .summary-icon {
    background: linear-gradient(135deg, $dangerColorDark 0%, lighten($dangerColorDark, 10%) 100%);
}
.dark .adjustment-summary-section .summary-card.net .summary-icon {
    background: linear-gradient(135deg, $secondaryColorDark 0%, lighten($secondaryColorDark, 10%) 100%);
}
.dark .adjustment-summary-section .summary-card.net.positive .summary-value { color: $successColorDark; }
.dark .adjustment-summary-section .summary-card.net.negative .summary-value { color: $dangerColorDark; }
.dark .adjustment-summary-section .summary-card.net.neutral .summary-value { color: rgba($darkTextColor, 0.7); }

.dark .confirmation-summary-header { 
    background: linear-gradient(135deg, lighten($darkCardBg, 3%) 0%, lighten($darkCardBg, 6%) 100%);
    border-color: $darkBorderColor;
}
.dark .stat-item { background: $darkCardBg; border-color: $darkBorderColor; }
.dark .stat-item .stat-label { color: rgba($darkTextColor, 0.75); }
.dark .stat-item .stat-value { color: $darkTextColor; }

.dark .high-positioned-modal ::v-deep .modal-header { border-bottom-color: $darkBorderColor !important; }
.dark .detail-item .detail-value.negative { color: $dangerColorDark; }
.dark .uniform-confirmation-actions { border-top-color: $darkBorderColor; }

.uniform-confirmation-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding-top: 20px;
    border-top: 1px solid $lightBorderColor;
}

// Responsive Design
@media (max-width: 1200px) {
    .custom-products-table {
        font-size: 0.8rem;
        
        thead th {
            padding: 12px 8px;
            font-size: 0.75rem;
        }
        
        tbody td {
            padding: 10px 8px;
        }
    }
    
    .summary-cards {
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    }
}

/* Tokenized spacing and paddings overrides for consistency */
.section-header { margin-bottom: $sectionGap; }
.product-search-section,
.products-table-section,
.adjustment-summary-section,
.action-buttons-section {
    border-radius: $cardRadius;
    padding: $cardPaddingY $cardPaddingX;
    margin-bottom: $sectionGap;
    box-shadow: $cardShadow;
}
.products-table-container { border-radius: $cardRadius; }
.custom-products-table thead th { padding: $tableCellPaddingY $tableCellPaddingX; }
.custom-products-table tbody td { padding: $tableCellPaddingY $tableCellPaddingX; }

@media (max-width: 768px) {
    .create-adjustment-container {
        margin: 0 -10px;
    }
    
    .product-search-section,
    .products-table-section,
    .adjustment-summary-section,
    .action-buttons-section {
        margin-left: 10px;
        margin-right: 10px;
        border-radius: 8px;
        padding: 16px;
    }
    
    .custom-products-table {
        font-size: 0.7rem;
        
        thead th {
            padding: 8px 4px;
            font-size: 0.65rem;
            
            i {
                display: none;
            }
        }
        
        tbody td {
            padding: 6px 4px;
        }
    }
    
    .price-adjustment-cell {
        .price-adjustment-container {
            gap: 4px;
            
            .current-price-row,
            .new-price-row {
                gap: 4px;
                
                .price-label {
                    min-width: 40px;
                    font-size: 0.65rem;
                }
                
                .compact-input {
                    max-width: 60px !important;
                }
            }
            
            .change-row {
                .change-container {
                    font-size: 0.65rem;
                    padding: 1px 4px;
                }
            }
        }
    }
    
    .reason-notes-cell {
        .reason-notes-container {
            gap: 4px;
        }
    }
    
    .summary-cards {
        grid-template-columns: 1fr;
        gap: 12px;
        
        .summary-card {
            padding: 16px;
            
            .summary-icon {
                width: 40px;
                height: 40px;
                
                i {
                    font-size: 1rem;
                }
            }
            
            .summary-value {
                font-size: 1.2rem;
            }
        }
    }
    
    .confirmation-summary {
        grid-template-columns: 1fr;
    }
    
    // Mobile responsive for confirmation modal
    .summary-stats {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
    }
    
    .stat-item {
        padding: 12px 8px;
        
        .stat-label {
            font-size: 0.7rem;
        }
        
        .stat-value {
            font-size: 1rem;
        }
    }
    
    .detail-grid {
        grid-template-columns: 1fr;
        gap: 8px;
    }
    
    .product-card-header {
        padding: 12px;
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }
    
    .product-card-body {
        padding: 12px;
    }
}
</style>
